import React, { useState, useEffect, useRef } from 'react';
import * as ReactDOM from 'react-dom/client';
import { environment } from '../environments/environment';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthData {
  token: string;
  platform: string;
  region: string;
  user_type: string;
}

export interface ChatbotOptions {
  /** Authentication object passed to the chatbot app on handshake. Required. */
  auth: AuthData;
  /**
   * Whether to show the floating chat toggle button (default: true).
   * When false, the button is hidden and the popup can only be
   * opened/closed programmatically via `chatbot.open()` / `chatbot.close()`.
   */
  showToggle?: boolean;
}

// ─── PostMessage event types (must mirror apps/chatbot/src/constants/messageEvents.ts) ───

const CHATBOT_READY = 'CHATBOT_READY';
const CHATBOT_AUTH  = 'CHATBOT_AUTH';

// ─── Constants ────────────────────────────────────────────────────────────────

const CONTAINER_ID = '__paymob_chatbot_root__';
const PRIMARY_COLOR_DEFAULT = '#144dff';

// ─── Styles injected into the host page ───────────────────────────────────────

function injectStyles(primaryColor: string) {
  const id = '__paymob_chatbot_styles__';
  if (document.getElementById(id)) return;

  const css = `
    #${CONTAINER_ID} * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

    .pmb-chat-toggle {
      position: fixed;
      bottom: 28px;
      right: 28px;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: ${primaryColor};
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 20px rgba(20, 77, 255, 0.35);
      z-index: 999998;
      transition: transform 0.2s, box-shadow 0.2s;
      outline: none;
    }
    .pmb-chat-toggle:hover {
      transform: scale(1.08);
      box-shadow: 0 6px 28px rgba(20, 77, 255, 0.45);
    }
    .pmb-chat-toggle:active { transform: scale(0.96); }

    .pmb-chat-toggle svg { transition: opacity 0.2s, transform 0.2s; }

    .pmb-chat-popup {
      position: fixed;
      bottom: 100px;
      right: 28px;
      width: 390px;
      height: 600px;
      border-radius: 20px;
      overflow: hidden;
      box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
      z-index: 999999;
      display: flex;
      flex-direction: column;
      transform-origin: bottom right;
      transition: opacity 0.25s ease, transform 0.25s ease;
      opacity: 0;
      transform: scale(0.85) translateY(12px);
      pointer-events: none;
    }
    .pmb-chat-popup.pmb-no-toggle {
      bottom: 28px;
    }
    .pmb-chat-popup.pmb-open {
      opacity: 1;
      transform: scale(1) translateY(0);
      pointer-events: all;
    }

    .pmb-chat-header {
      background: ${primaryColor};
      padding: 14px 18px;
      display: flex;
      align-items: center;
      gap: 10px;
      flex-shrink: 0;
    }
    .pmb-chat-header-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: rgba(255,255,255,0.2);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .pmb-chat-header-info { flex: 1; }
    .pmb-chat-header-name {
      color: #fff;
      font-size: 15px;
      font-weight: 600;
      margin: 0;
      line-height: 1.2;
    }
    .pmb-chat-header-status {
      color: rgba(255,255,255,0.8);
      font-size: 12px;
      margin: 2px 0 0;
    }
    .pmb-chat-header-close {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.8;
      transition: opacity 0.15s, background 0.15s;
    }
    .pmb-chat-header-close:hover { opacity: 1; background: rgba(255,255,255,0.15); }

    .pmb-chat-iframe {
      flex: 1;
      border: none;
      width: 100%;
      background: #fff;
    }

    @media (max-width: 480px) {
      .pmb-chat-popup,
      .pmb-chat-popup.pmb-no-toggle {
        bottom: 0;
        right: 0;
        width: 100vw;
        height: 100dvh;
        border-radius: 0;
      }
      .pmb-chat-toggle {
        bottom: 20px;
        right: 20px;
      }
    }
  `;

  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

// ─── React Component ──────────────────────────────────────────────────────────

interface ChatbotWidgetProps {
  chatbotUrl: string;
  primaryColor: string;
  showToggle: boolean;
  auth: AuthData;
  initialOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

function ChatbotWidget({ chatbotUrl, primaryColor, showToggle, auth, initialOpen = false, onOpen, onClose }: ChatbotWidgetProps) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Expose open/close to the SDK instance
  useEffect(() => {
    (window as any).__paymobChatbotOpen = () => setIsOpen(true);
    (window as any).__paymobChatbotClose = () => setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) onOpen?.();
    else onClose?.();
  }, [isOpen]);

  // Listen for CHATBOT_READY from the iframe, then send auth
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === CHATBOT_READY && iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: CHATBOT_AUTH, auth },
          chatbotUrl
        );
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [auth, chatbotUrl]);

  const toggle = () => setIsOpen((v) => !v);
  const close = () => setIsOpen(false);

  return (
    <>
      {/* Floating toggle button — hidden when showToggle is false */}
      <button
        className="pmb-chat-toggle"
        onClick={toggle}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        style={{ background: primaryColor, display: showToggle ? 'flex' : 'none' }}
      >
        {isOpen ? (
          // Close (X) icon
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        ) : (
          // Chat bubble icon
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path
              d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
              fill="#fff"
            />
          </svg>
        )}
      </button>

      {/* Popup panel */}
      <div className={`pmb-chat-popup${!showToggle ? ' pmb-no-toggle' : ''}${isOpen ? ' pmb-open' : ''}`} role="dialog" aria-modal="true" aria-label="Paymob AI Chat">
        {/* Header */}
        <div className="pmb-chat-header">
          <div className="pmb-chat-header-avatar">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 168 148" fill="none">
              <path d="M101.158 0C84.9501 0 69.972 5.38183 60.0239 14.2394L60.1357 1.90607H45.7165H29.6208L17.6607 57.0697C14.7545 72.4303 25.1497 74.2243 30.515 74.7849L30.2914 75.1212C24.9261 75.7939 13.4131 78.9333 10.7305 91.8273L4.69456 122.885C4.35923 124.791 0 148 0 148H16.8782H18.3313C28.2794 148 36.8862 140.936 38.7864 131.182L45.1576 99.7879C51.8642 106.739 63.0419 110.327 75.4491 110.327C109.317 110.327 136.032 87.9031 142.738 54.7152C149.445 21.8637 133.014 0 101.158 0ZM105.629 55.6121C102.723 70.0758 90.3154 80.3909 75.002 80.3909C59.3533 80.3909 51.0818 70.6364 54.0998 55.6121L54.4351 53.8182C57.2295 40.3636 68.966 30.0485 84.8383 30.0485C100.152 30.0485 108.758 40.3636 105.629 55.6121Z" fill="#fff"/>
              <path d="M147.992 3.36364V2.01819H155.928V3.36364H152.687V12.3333H151.122V3.36364H147.992Z" fill="#fff"/>
              <path d="M157.604 2.01819H159.505L162.746 9.9788H162.858L166.099 2.01819H168V12.3333H166.547V4.93335H166.435L163.417 12.3333H162.187L159.169 4.93335H159.058V12.3333H157.604V2.01819Z" fill="#fff"/>
            </svg>
          </div>
          <div className="pmb-chat-header-info">
            <p className="pmb-chat-header-name">Paymob Intelligence</p>
            <p className="pmb-chat-header-status">● Online</p>
          </div>
          <button className="pmb-chat-header-close" onClick={close} aria-label="Close chat">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Chatbot iframe */}
        <iframe
          ref={iframeRef}
          className="pmb-chat-iframe"
          src={chatbotUrl}
          title="Paymob AI Chat"
          allow="microphone"
        />
      </div>
    </>
  );
}

// ─── SDK Class ────────────────────────────────────────────────────────────────

interface ResolvedOptions {
  auth: AuthData;
  chatbotUrl: string;
  primaryColor: string;
  showToggle: boolean;
}

export class Chatbot {
  private root: ReactDOM.Root | null = null;
  private options: ResolvedOptions | null = null;
  private isOpen = false;

  constructor(options: ChatbotOptions) {
    const authErrors = Chatbot.validateAuth(options.auth);
    if (authErrors.length > 0) {
      authErrors.forEach((msg) => console.error(`[PaymobChatbot] ${msg}`));
      return;
    }

    this.options = {
      auth: options.auth,
      chatbotUrl: environment.CHATBOT_URL,
      primaryColor: PRIMARY_COLOR_DEFAULT,
      showToggle: options.showToggle ?? true,
    };

    this.mount();
  }

  private static validateAuth(auth: unknown): string[] {
    const errors: string[] = [];

    if (!auth || typeof auth !== 'object' || Array.isArray(auth)) {
      errors.push('auth must be a plain object with { token, platform, region }.');
      return errors;
    }

    const obj = auth as Record<string, unknown>;
    const required: (keyof AuthData)[] = ['token', 'platform', 'region', 'user_type'];

    for (const key of required) {
      if (!(key in obj)) {
        errors.push(`auth.${key} is required but missing.`);
      } else if (typeof obj[key] !== 'string') {
        errors.push(`auth.${key} must be a string, got ${typeof obj[key]}.`);
      } else if ((obj[key] as string).trim() === '') {
        errors.push(`auth.${key} must not be an empty string.`);
      }
    }

    const allowed = new Set(required);
    for (const key of Object.keys(obj)) {
      if (!allowed.has(key as keyof AuthData)) {
        errors.push(`auth contains unexpected key "${key}". Allowed keys: token, platform, region.`);
      }
    }

    return errors;
  }

  private mount() {
    if (!this.options) return;
    // Inject styles
    injectStyles(this.options.primaryColor);

    // Create or reuse container
    let container = document.getElementById(CONTAINER_ID);
    if (!container) {
      container = document.createElement('div');
      container.id = CONTAINER_ID;
      document.body.appendChild(container);
    }

    this.root = ReactDOM.createRoot(container);
    this.render(false);
  }

  private render(open: boolean) {
    if (!this.options) return;
    this.root?.render(
      <ChatbotWidget
        chatbotUrl={this.options.chatbotUrl}
        primaryColor={this.options.primaryColor}
        showToggle={this.options.showToggle}
        auth={this.options.auth}
        initialOpen={open}
        onOpen={() => { this.isOpen = true; }}
        onClose={() => { this.isOpen = false; }}
      />
    );
  }

  /** Open the chat popup */
  open() {
    (window as any).__paymobChatbotOpen?.();
  }

  /** Close the chat popup */
  close() {
    (window as any).__paymobChatbotClose?.();
  }

  /** Destroy the widget and remove it from the DOM */
  destroy() {
    this.root?.unmount();
    document.getElementById(CONTAINER_ID)?.remove();
    document.getElementById('__paymob_chatbot_styles__')?.remove();
    this.root = null;
  }
}
